import React, { FunctionComponent, useEffect, useState } from "react";
import MaterialTable from "@material-table/core";
import moment from "moment";
import {
  MergeStoreOrders,
  RemoveOrder,
  Store,
  StoreOrder,
  StoreOrderState as OrderStateType
} from "../../../models/Store";
import { StoreOrderState } from "../components/StoreOrderState";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../store";
import {
  thunkFetchStoreOrders,
  thunkRemoveStoreOrders
} from "../../../store/stores/actions";
import { ConfirmDialog } from "../../../components/dialogs/ConfirmDialog";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  TextField
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import useStyles from "./styles";
import { ProblemDetails } from "../../../models/Http";
import * as StoreOrderApi from "../../../api/storeOrderApi";
import { isOk } from "../../../result";
import Alert from "@material-ui/lab/Alert";
import Modal from "../../../components/dialogs/modal";
import { StoreSelect } from "../../../components/selects/StoreSelect";
import { OrderItemsTable } from "./components/OrderItemsTable";
import { useParams } from "react-router-dom";
import { StoreOrdersState } from "../../../store/stores/types";
import { useI18n } from "../../../hooks/useI18n";

interface StoreOrderView {
  raw: StoreOrder;
  estimatedDelivery?: Date;
  deadline: Date;
  stores: string;
  distributors: string;
  state: OrderStateType;
}

interface State {
  data: StoreOrderView[];
  orderToSave?: StoreOrder;
  isLoading: boolean;
  isMerging: boolean;
  showSaveDialog: boolean;
  popupError?: string;
  problem?: ProblemDetails;
  toBeMerged?: MergeStoreOrders;
  ordersToRmove?: RemoveOrder[];
  removeOrderConfirmDialogVisible: boolean;
  createOrderProposalModalVisible: boolean;
  createOrderSuccess: boolean;
  saveOrderSuccess: boolean;
}

interface OrdersTableProps {
  storeId: string | "all";
}

export const StoreOrders = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useI18n();

  const params = useParams();

  const [state, setState] = useState<State>({
    isLoading: true,
    showSaveDialog: false,
    isMerging: false,
    createOrderSuccess: false,
    removeOrderConfirmDialogVisible: false,
    createOrderProposalModalVisible: false,
    saveOrderSuccess: false,
    data: []
  });

  const ordersState = useSelector<AppState, StoreOrdersState>(
    s => s.storeOrders
  );

  const { orders } = ordersState;

  const [selectedStore, setSelctedStore] = useState<Store>();
  const [selectedStoreDistributors, setSelectedStoreDistributors] = useState<
    string[]
  >([]);

  useEffect(() => {
    dispatch(
      thunkFetchStoreOrders(params.storeId || "all", [
        OrderStateType.Proposed,
        OrderStateType.Confirmed,
        OrderStateType.Ordered
      ])
    );
  }, [state.isLoading, params]);

  useEffect(() => {
    if (state.isMerging && state.toBeMerged) {
      const toBeMerged = state.toBeMerged;
      setState({ ...state, toBeMerged: undefined });

      StoreOrderApi.mergeOrders(toBeMerged).then(result => {
        if (isOk(result)) {
          return setState({
            ...state,
            isMerging: false,
            isLoading: true,
            toBeMerged: undefined
          });
        }

        return setState({
          ...state,
          isMerging: false,
          toBeMerged: undefined,
          problem: result.details
        });
      });
    }
  }, [state.isMerging]);

  useEffect(() => {
    setState({
      ...state,
      isLoading: false,
      data:
        orders && orders.length
          ? orders.slice().map(o => ({
              raw: o,
              estimatedDelivery: o.estimatedDelivery,
              deadline:
                o.state === OrderStateType.Proposed
                  ? o.confirmDeadline
                  : o.orderDeadline,
              state: o.state,
              stores: o.stores.map(s => s.name).join(", "),
              distributors: o.distributors.map(s => s.name).join(", ")
            }))
          : []
    });
  }, [orders]);

  const handleMerge = (): boolean => {
    if (!state.toBeMerged) {
      return false;
    }

    setState({ ...state, isMerging: true });
    return true;
  };

  return (
    <Box position={"relative"}>
      {ordersState.isLoading && (
        <div className={classes.spinnerContainer}>
          <CircularProgress />
        </div>
      )}
      <MaterialTable
        title=""
        columns={[
          {
            title: "EstimatedDelivery",
            field: "estimatedDelivery",
            render: rowData =>
              moment(rowData.estimatedDelivery).format("YYYY-MM-DD HH:mm")
          },
          {
            title: "Deadline",
            field: "deadline",
            render: rowData =>
              moment(rowData.deadline).format("YYYY-MM-DD HH:mm")
          },
          {
            title: "Store(s)",
            field: "stores"
          },
          {
            title: "Distributor(s)",
            field: "distributors"
          },
          {
            title: "State",
            field: "state",
            render: rowData => <StoreOrderState state={rowData.state} />
          },
          {
            title: "",
            render: row => {
              return (
                <div>
                  <IconButton
                    aria-label="close"
                    disabled={row.state === OrderStateType.Ordered}
                    onClick={() => {
                      setState({
                        ...state,
                        showSaveDialog: true,
                        orderToSave: row.raw
                      });
                    }}
                  >
                    <SaveIcon />
                  </IconButton>
                </div>
              );
            }
          }
        ]}
        data={state.data}
        isLoading={state.isLoading || state.isMerging}
        detailPanel={(row: StoreOrderView) => {
          return <OrderItemsTable order={row.raw} />;
        }}
        options={{
          search: true,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50, 100],
          selection: true
        }}
        actions={[
          {
            icon: "add_circle",
            tooltip: "Create a new order proposal",
            iconProps: {
              style: { color: "rgb(31, 132, 52)", fontSize: "2rem" }
            },
            isFreeAction: true,
            onClick: () =>
              setState({ ...state, createOrderProposalModalVisible: true })
          },
          {
            icon: "delete",
            tooltip: "Remove",
            onClick: (event, rowData: any) => {
              setState({
                ...state,
                removeOrderConfirmDialogVisible: true,
                ordersToRmove: rowData.map((rd: any) => {
                  const {
                    raw: { id, isMerged }
                  } = rd;

                  return {
                    id,
                    isMerged
                  };
                })
              });
            }
          },
          {
            tooltip: t("common.actions.mergeOrders"),
            icon: "merge_type",
            onClick: (evt, data: any): data is StoreOrderView[] => {
              const orders: StoreOrderView[] = data;

              if (orders.length == 1) {
                setState({
                  ...state,
                  popupError: "Need more than 1 order when merging."
                });
                return true;
              }

              for (let i = 0; i < orders.length; i++) {
                if (orders[i].state !== OrderStateType.Confirmed) {
                  setState({
                    ...state,
                    popupError: "Can only merge confirmed orders."
                  });
                  return true;
                }
                if (orders[i].raw.isMerged) {
                  setState({
                    ...state,
                    popupError:
                      "Can only merge orders that are not previously merged."
                  });
                  return true;
                }
              }

              setState({
                ...state,
                toBeMerged: { storeOrderIds: orders.map(i => i.raw.id) }
              });
              return true;
            }
          }
        ]}
      />
      <ConfirmDialog
        visible={state.removeOrderConfirmDialogVisible}
        titleText={"Remove order(s)?"}
        confirmText={"Yes"}
        cancelText={"No"}
        onCancel={() =>
          setState({ ...state, removeOrderConfirmDialogVisible: false })
        }
        onConfirm={() => {
          if (state.ordersToRmove) {
            dispatch(thunkRemoveStoreOrders(state.ordersToRmove));
            setState({ ...state, removeOrderConfirmDialogVisible: false });
          }
          return true;
        }}
      >
        Are you sure you want to remove the slected order(s)?
      </ConfirmDialog>
      <ConfirmDialog
        visible={state.popupError !== undefined}
        titleText={t("common.messages.invalidMerge")}
        onCancel={() => setState({ ...state, popupError: undefined })}
        onConfirm={() => {
          setState({ ...state, popupError: undefined });
          return true;
        }}
        showCancelButton={false}
        bodyText={state.popupError}
      />
      <ConfirmDialog
        visible={state.toBeMerged !== undefined}
        titleText={t("common.messages.confirmMerge")}
        confirmText={"Yes"}
        cancelText={"No"}
        onCancel={() => setState({ ...state, toBeMerged: undefined })}
        onConfirm={handleMerge}
      >
        <p>
          Are you sure you want to merge the{" "}
          {state.toBeMerged?.storeOrderIds?.length} selected orders?
        </p>

        <TextField
          className={classes.field}
          label="Comment"
          onChange={e => {
            if (!state.toBeMerged) {
              return;
            }

            return setState({
              ...state,
              toBeMerged: {
                ...state.toBeMerged,
                comment: e.target.value
              }
            });
          }}
        />
      </ConfirmDialog>
      <Modal
        title={t("common.messages.generateNewOrderProposal")}
        open={state.createOrderProposalModalVisible}
        actions={[
          {
            text: "Generate",
            type: "ok",
            disable: () => {
              if (!selectedStore) {
                return true;
              }

              if (selectedStoreDistributors.length == 0) {
                return true;
              }

              return false;
            },
            action: async () => {
              if (selectedStore) {
                const result = await StoreOrderApi.generateOrder(
                  selectedStore.id,
                  selectedStoreDistributors
                );
                if (result.type == "OK") {
                  setState({
                    ...state,
                    createOrderSuccess: true,
                    createOrderProposalModalVisible: false
                  });
                } else {
                  setState({
                    ...state,
                    problem: result.details,
                    createOrderSuccess: false
                  });
                }
              }
            }
          },
          {
            text: "Cancel",
            type: "cancel",
            action: () => {
              setSelctedStore(undefined);
              setSelectedStoreDistributors([]);
              setState({
                ...state,
                createOrderProposalModalVisible: false
              });
            }
          }
        ]}
      >
        <div>
          <StoreSelect
            className={classes.storeSelect}
            filterOpened={true}
            onSelected={store => {
              setSelctedStore(store);
              setSelectedStoreDistributors(
                store.distributors.map(d => d.distributor.id)
              );
            }}
          ></StoreSelect>
        </div>
        <div>
          {selectedStore && (
            <FormControl
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 10
              }}
            >
              <InputLabel id="distributor-multi-select-label">
                Select one or more distributors
              </InputLabel>
              <Select
                multiple
                value={selectedStoreDistributors}
                onChange={(event: any) => {
                  setSelectedStoreDistributors(event.target.value);
                }}
                labelId="distributor-multi-select-label"
                id="distributor-multi-select"
                autoWidth={true}
                input={<Input />}
                renderValue={value => {
                  return selectedStore.distributors
                    .filter(sd =>
                      selectedStoreDistributors.includes(sd.distributor.id)
                    )
                    .map(sd => sd.distributor.name)
                    .join(", ");
                }}
              >
                {selectedStore &&
                  selectedStore.distributors &&
                  selectedStore.distributors.length > 0 &&
                  selectedStore.distributors.map(x => (
                    <MenuItem key={x.distributor.id} value={x.distributor.id}>
                      <Checkbox
                        checked={
                          selectedStoreDistributors.indexOf(x.distributor.id) >
                          -1
                        }
                      />
                      <ListItemText primary={x.distributor.name} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}
        </div>
      </Modal>

      <div className={classes.createOrderResult}>
        <Snackbar open={state.createOrderSuccess} autoHideDuration={6000}>
          <Alert severity="success">
            Your order has been generated, it will show up in this list in a few
            minutes!
          </Alert>
        </Snackbar>
        {state.problem && (
          <Snackbar open={!state.createOrderSuccess} autoHideDuration={6000}>
            <Alert severity="error">
              {state.problem.status}, {state.problem.title},{" "}
              {state.problem.detail}
            </Alert>
          </Snackbar>
        )}
      </div>

      <div className={classes.createOrderResult}>
        <Snackbar open={state.saveOrderSuccess} autoHideDuration={6000}>
          <Alert severity="success">
            {t("common.messages.orderHasBeenSaved")}
          </Alert>
        </Snackbar>
      </div>

      <ConfirmDialog
        visible={state.showSaveDialog}
        titleText={t("common.actions.saveOrder")}
        confirmText={"Save"}
        onConfirm={async () => {
          if (state.orderToSave) {
            let newState =
              state.orderToSave.state === OrderStateType.Confirmed
                ? OrderStateType.Ordered
                : OrderStateType.Confirmed;

            const result = await StoreOrderApi.updateOrder(
              state.orderToSave?.id,
              newState
            );

            if (result.type === "ERROR") {
              setState({ ...state, problem: result.details });
            } else {
              setState({
                ...state,
                saveOrderSuccess: true,
                showSaveDialog: false,
                data: state.data.map(o => {
                  if (o.raw.id === state.orderToSave?.id) {
                    o.state = newState;
                  }
                  return o;
                })
              });
            }
          }
          return true;
        }}
        onCancel={() => {
          setState({ ...state, showSaveDialog: false });
        }}
      >
        {state?.orderToSave?.state === OrderStateType.Proposed ? (
          <>
            <p>{t("common.messages.confirmingOrder")}</p>
            <p>
              Press <b>CONFIRM</b> to continue, or <b>CANCEL</b> if there are
              more changes to be done.
            </p>
          </>
        ) : null}
        {state?.orderToSave?.state === OrderStateType.Confirmed ? (
          <>
            <p>
              When saving the order it will be placed in a state of{" "}
              <b>ORDERED</b> and can later be picked up in the delivery.
            </p>
            <p>
              Press <b>SAVE</b> to continue, or <b>CANCEL</b> if the order is
              not ready.
            </p>
          </>
        ) : null}
      </ConfirmDialog>
    </Box>
  );
};
